import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, CreditCard, CheckCircle2, Clock, Sparkles, ArrowRight, Loader2, Receipt, ExternalLink, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { api } from '../utils/api';

const Billing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.getBilling().then(setData).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [user, navigate]);

  const handleManageBilling = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Could not open billing portal');
      setOpeningPortal(false);
    }
  };

  const handleSyncPro = async () => {
    setSyncing(true);
    try {
      const res = await api.syncProFromStripe();
      if (res.ok && res.plan) {
        toast.success(`Synced! You're now on Pro (${res.plan}). Reloading…`);
        setTimeout(() => window.location.reload(), 1200);
      } else if (res.ok && res.active === false) {
        toast.info('No active subscription found in Stripe for your email. If you paid with a different email, log in to that account instead.');
      } else {
        toast.error(res.error || 'No active subscription found.');
      }
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const isPro = data.plan === 'pro';
  const isTrial = data.billing_cycle === 'trial';
  const isLifetime = data.billing_cycle === 'grandfathered';
  const hasStripeSub = !!data.has_stripe_subscription;
  const willCancel = !!data.cancel_at_period_end;
  const planLabel = isLifetime
    ? 'Lifetime Pro'
    : isTrial
      ? 'Pro Trial'
      : isPro
        ? `Pro — ${data.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}`
        : 'Free';
  const endsDate = data.subscription_ends_at ? new Date(data.subscription_ends_at) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      <Helmet><title>Billing & Subscription — LinkMonkey</title></Helmet>

      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/settings" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to settings
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Billing</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Subscription & payments</h1>
        </div>

        {/* Current plan */}
        <Card className={`p-6 border-2 ${isPro ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-amber-500/30 bg-amber-500/[0.03]'}`} data-testid="current-plan-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPro ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'}`}>
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold">{planLabel}</h2>
                  {isTrial && <Badge className="bg-amber-500 hover:bg-amber-600">Trial</Badge>}
                  {isLifetime && <Badge className="bg-purple-500 hover:bg-purple-600">Lifetime</Badge>}
                  {willCancel && <Badge variant="outline" className="border-amber-500/50 text-amber-600">Cancels at period end</Badge>}
                </div>
                {isPro && data.days_remaining !== null && !isLifetime && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {willCancel
                      ? <>Ends on <strong>{endsDate.toLocaleDateString()}</strong> — <span className="text-amber-600">no further charges</span></>
                      : data.days_remaining > 0
                        ? <>Renews on <strong>{endsDate.toLocaleDateString()}</strong> — auto-renewing</>
                        : <>Expired on {endsDate.toLocaleDateString()}</>}
                  </p>
                )}
                {!isPro && (
                  <p className="text-sm text-muted-foreground mt-0.5">Upgrade to unlock all features.</p>
                )}
              </div>
            </div>
          </div>

          {/* CTAs */}
          {isLifetime ? (
            <p className="text-sm text-muted-foreground">No billing required — you're on Pro forever. Thanks for being an early supporter.</p>
          ) : hasStripeSub ? (
            <div className="space-y-3">
              {willCancel ? (
                <div className="rounded-lg bg-amber-500/[0.06] border border-amber-500/30 p-3 text-sm text-amber-700 dark:text-amber-300 flex gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>You've scheduled cancellation. Pro stays active until <strong>{endsDate?.toLocaleDateString()}</strong>. Change your mind? Reactivate in the portal below.</span>
                </div>
              ) : (
                <div className="rounded-lg bg-background/60 border border-border p-3 text-sm text-muted-foreground flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-foreground">Auto-renewing.</strong> Manage your card, change plan, or cancel anytime — no friction, no email required.</span>
                </div>
              )}
              <Button onClick={handleManageBilling} disabled={openingPortal} className="w-full sm:w-auto" data-testid="manage-billing-btn">
                {openingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Manage subscription
                <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-70" />
              </Button>
              <p className="text-xs text-muted-foreground">Opens Stripe's secure billing portal.</p>
            </div>
          ) : isPro && isTrial ? (
            <Link to="/pricing">
              <Button data-testid="upgrade-billing-btn">
                Subscribe before trial ends <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          ) : (
            <Link to="/pricing">
              <Button data-testid="upgrade-billing-btn">
                {data.trial_used ? 'Subscribe to Pro' : 'Start 7-day free trial'} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          )}
        </Card>

        {/* Stats */}
        {data.payment_count > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Lifetime spend</p>
              <p className="text-2xl font-semibold tracking-tight" data-testid="lifetime-spent">€{data.lifetime_spent.toFixed(2)}</p>
            </Card>
            <Card className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Payments</p>
              <p className="text-2xl font-semibold tracking-tight" data-testid="payment-count">{data.payment_count}</p>
            </Card>
          </div>
        )}

        {/* Payment history */}
        <Card className="p-6" data-testid="payment-history-card">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Payment history</h2>
          </div>
          {data.history.length === 0 ? (
            <div className="text-center py-8 rounded-lg bg-muted/20 border border-dashed border-border">
              <Receipt className="w-7 h-7 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No payments yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.history.map((h, i) => (
                <div key={h.invoice_id || h.session_id || i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 text-sm" data-testid={`payment-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {h.status === 'paid' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium capitalize">
                        {h.kind === 'renewal' ? 'Renewal' : 'Pro'} — {h.plan || 'subscription'}
                        {h.status !== 'paid' && <span className="ml-2 text-xs text-amber-600">({h.status})</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {h.created_at ? new Date(h.created_at).toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </p>
                    </div>
                  </div>
                  <span className="font-semibold tabular-nums">€{(h.amount || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help */}
        <Card className="p-5">
          <p className="text-sm text-muted-foreground">
            Billing question or refund request? <a href="mailto:hello@linkmonkey.io" className="text-primary hover:underline font-medium">Email us</a> — we usually reply within 24 hours.
          </p>
        </Card>
      </main>
    </div>
  );
};

export default Billing;
